import type {Control, FieldErrors, FieldValues, Path} from "react-hook-form";
import {Splitter} from "../layout/Splitter.js";
import {AddressGeocodeStatus} from "./AddressGeocodeStatus.js";
import {FloatingField} from "./FloatingField.js";
import type {
  AddressVerification,
  MapCenterPrecision,
  StructuredReplacementFields,
} from "./useAddressVerification.js";

/** Chemins RHF des quatre champs d'adresse dans le schéma du formulaire appelant. */
export type AddressFieldNames<T extends FieldValues> = {
  line1: Path<T>;
  line2: Path<T>;
  zip: Path<T>;
  city: Path<T>;
};

const DEFAULT_LABELS = {
  line1: "Adresse",
  line2: "Complément d'adresse",
  zip: "Code postal",
  city: "Ville",
};

type Props<T extends FieldValues> = {
  /** Machine à états (`useAddressVerification`), tenue par le formulaire appelant. */
  verification: AddressVerification;
  /**
   * Écrit rue/CP/ville dans le formulaire RHF au clic « Remplacer » du
   * callout « Adresse proche trouvée » (#980) — via `setValue` côté appelant,
   * le complément n'est jamais touché. Voir
   * `AddressGeocodeStatus`/`useAddressVerification.structuredReplacement`.
   * Optionnel — absent, le bouton « Remplacer » ne s'affiche pas.
   */
  onReplace?: (fields: StructuredReplacementFields) => void;
  control: Control<T>;
  idPrefix?: string;
  names: AddressFieldNames<T>;
  errors: FieldErrors<T>;
  /** Libellés des champs — défauts français, à surcharger pour un formulaire i18n. */
  labels?: Partial<typeof DEFAULT_LABELS>;
  /**
   * Verrous PROPRES à l'appelant (sauvegarde en cours, toggle siège…) — le
   * gel manuel (#956) et le masquage en mode carte (#929) sont déjà appliqués
   * ici, ne pas les redonner.
   */
  disabled?: boolean;
  /** Répartition CP/ville du `Splitter` en % — défaut 49/49. */
  zipCitySplit?: {zip: number; city: number};
  /**
   * Garde-fou de précision du centrage carte (#1022) — threading vers
   * `AddressGeocodeStatus`/`ManualPinPlacement`. Absent = pas de warning :
   * SEUL le tunnel public le passe (`AddressStep`, calculé depuis
   * `verification.mapCenterPrecision`) ; les formulaires authentifiés ne le
   * passent pas, aucun changement de comportement pour eux.
   */
  mapCenterPrecision?: MapCenterPrecision | null;
};

/**
 * Section adresse vérifiée complète : champs texte, badge/callout de statut
 * (`AddressGeocodeStatus`, verdict `verification.resolved` à brancher sur le
 * `disabled` du bouton de soumission de l'appelant), message de correction.
 * Un formulaire appelant n'a besoin de connaître QUE cette section et
 * `useAddressVerification` — un point de contact par surface.
 */
export function AddressVerificationSection<T extends FieldValues>({
  verification,
  onReplace,
  control,
  idPrefix,
  names,
  errors,
  labels,
  disabled,
  zipCitySplit,
  mapCenterPrecision,
}: Props<T>) {
  const fieldLabels = {...DEFAULT_LABELS, ...labels};
  /**
   * Champs texte verrouillés (deux verrous génériques, en plus de ceux de
   * l'appelant) :
   * 1. Gel manuel actif (#956) — « Retirer le repère » (dans le badge) est
   *    l'unique retour à l'édition.
   * 2. Mode carte : les champs sont carrément masqués (#929) — la carte se
   *    centre une seule fois au montage, une édition du texte pendant qu'elle
   *    est ouverte désynchroniserait le pin posé de l'adresse affichée à côté.
   */
  const fieldsDisabled = disabled || verification.isManualFrozen;

  return (
    <>
      {!verification.mapMode && (
        <>
          <FloatingField
            idPrefix={idPrefix}
            name={names.line1}
            label={fieldLabels.line1}
            control={control}
            error={fieldError(errors, names.line1)}
            disabled={fieldsDisabled}
            required
          />
          <FloatingField
            idPrefix={idPrefix}
            name={names.line2}
            label={fieldLabels.line2}
            control={control}
            disabled={fieldsDisabled}
          />
          <Splitter width1={zipCitySplit?.zip} width2={zipCitySplit?.city}>
            <FloatingField
              idPrefix={idPrefix}
              name={names.zip}
              label={fieldLabels.zip}
              control={control}
              error={fieldError(errors, names.zip)}
              disabled={fieldsDisabled}
              required
              normalize={stripWhitespace}
            />
            <FloatingField
              idPrefix={idPrefix}
              name={names.city}
              label={fieldLabels.city}
              control={control}
              error={fieldError(errors, names.city)}
              disabled={fieldsDisabled}
              required
            />
          </Splitter>
        </>
      )}
      <AddressGeocodeStatus
        verification={verification}
        onReplace={onReplace}
        mapCenterPrecision={mapCenterPrecision}
      />
      {verification.correctionHint && (
        <div className="text-danger" style={{fontSize: "13px", marginTop: "-4px"}}>
          {verification.correctionHint}
        </div>
      )}
    </>
  );
}

// Les fautes d'espace dans un code postal ne doivent jamais échouer la validation stricte.
function stripWhitespace(value: string): string {
  return value.replace(/\s/g, "");
}

/**
 * `FieldErrors<T>` n'est pas indexable par un `Path<T>` arbitraire aux yeux
 * de TS (chemins imbriqués) — nos quatre champs sont plats, le cast est sûr.
 */
function fieldError<T extends FieldValues>(
  errors: FieldErrors<T>,
  name: Path<T>
): {message?: string} | undefined {
  return errors[name as keyof FieldErrors<T>] as {message?: string} | undefined;
}
